import React, { useState, useEffect } from "react";
import { Window } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { TextArea } from "@progress/kendo-react-inputs";
import { useDispatch } from "react-redux";

import "./PayRequestDetailed.scss";
import StepProgressBar, { mapApiDataToStepDetails } from "src/components/StepProgressBar";
import FileViewerDialog from "./FileViewerDialog";
import { getUserCredential } from "../../../lib/cookies";
import { getTimesheetDetails } from "../services/getTimesheetDetails";
import { getTimesheetProgressDetails } from "../services/getTimesheetProgressDetails";
import { downloadFile } from "../services/downloadFile";
import { setNewMessage } from "../../../redux/actions/setting-actions";
import { LoadingPanel } from "../../../components/loadingPanel";
import { convertHexToRgba } from "../../../utils/utils";
import { DialogComponent } from "src/components/dialog";
import { APPROVE_BTN_ACTIONS, REJECT_BTN_ACTIONS } from "src/constants/constants";
import { insertTimesheetApproval } from "../services/insertTimesheetApproval";
import { getUserTimezone } from "../../providerPayRequestDashboard/utils/dateUtils";
import { formatTimesheetQuantity } from "../../providerPayRequestDashboard/utils/thresholdQuantity";
import { convertTimeZone } from "src/lib/date-time.util";

// contract_limit_exceeded_type: 1 amount, 2 quantity, 3 both
const CONTRACT_LIMIT_MESSAGES = {
  1: "The calculated amount is above contractual limit",
  2: "The entered quantity is above contractual limit",
  3: "The entered quantity and calculated amount are above contractual limit",
};

const PayRequestDetailed = ({ visible, onClose, timesheetMstrKey, onRefresh, writeAccess = 0 }) => {
  const dispatch = useDispatch();
  const cookiesData = getUserCredential();

  const [timesheetDetails, setTimesheetDetails] = useState(null);
  const [progressDetails, setProgressDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fileViewerVisible, setFileViewerVisible] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isStatusBadgeVisible, setIsStatusBadgeVisible] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({});

  // State for approve/reject functionality
  const [comments, setComments] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmationPopup, setConfirmationPopup] = useState({
    title: "",
    desc: "",
    isVisible: false,
    type: "",
    actionBtn: [],
  });

  // Fetch data when component mounts or timesheetMstrKey changes
  useEffect(() => {
    const fetchData = async () => {
      if (!timesheetMstrKey || !visible) return;

      setIsLoading(true);
      try {
        const [timesheetData, progressData] = await Promise.all([
          getTimesheetDetails(cookiesData, dispatch, timesheetMstrKey),
          getTimesheetProgressDetails(cookiesData, dispatch, timesheetMstrKey),
        ]);

        setTimesheetDetails(timesheetData);
        setProgressDetails(progressData);
        setIsStatusBadgeVisible(true);
      } catch (error) {
        console.error("Error fetching timesheet details:", error);
        dispatch(
          setNewMessage({
            message: "Failed to fetch timesheet details",
            type: "Error",
            show: true,
          })
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [timesheetMstrKey, visible]);

  const formatDate = (date) => {
    if (!date) return "N/A";
    return (
      convertTimeZone({
        date,
        convertTimeZone: getUserTimezone(),
        outputFormat: "MM/dd/yyyy",
      }) || "N/A"
    );
  };

  const formatProviderLocalDate = (date) => {
    if (!date) return "N/A";
    return (
      convertTimeZone({
        date,
        convertTimeZone: timesheetDetails?.local_timezone || "America/New_York",
        outputFormat: "MM/dd/yyyy",
      }) || "N/A"
    );
  };

  const formatBudget = (budget) => {
    if (budget === null || budget === undefined) return "0.00";
    return Number(budget).toFixed(2);
  };

  // Utility function to format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // Utility function to get file icon based on MIME type
  const getFileIcon = (mimeType) => {
    if (!mimeType) return "fa-file";

    const type = mimeType.toLowerCase();
    if (type.startsWith("image/")) return "fa-file-image";
    if (type === "application/pdf") return "fa-file-pdf";
    if (type.includes("word") || type.includes("document")) return "fa-file-word";
    if (type.includes("excel") || type.includes("spreadsheet")) return "fa-file-excel";
    if (type.includes("text")) return "fa-file-alt";
    return "fa-file";
  };

  // Handle file view
  const handleFileView = (file) => {
    setSelectedFile(file);
    setFileViewerVisible(true);
  };

  // Handle file download
  const handleFileDownload = async (file) => {
    try {
      setDownloadProgress((prev) => ({ ...prev, [file.document_name]: 0 }));

      await downloadFile(file, (percentage) => {
        setDownloadProgress((prev) => ({ ...prev, [file.document_name]: percentage }));
      });

      // Clear progress after a delay
      setTimeout(() => {
        setDownloadProgress((prev) => {
          const newProgress = { ...prev };
          delete newProgress[file.document_name];
          return newProgress;
        });
      }, 1000);
    } catch (error) {
      console.error("Error downloading file:", error);
      dispatch(
        setNewMessage({
          message: "Failed to download file",
          type: "error",
        })
      );
      setDownloadProgress((prev) => {
        const newProgress = { ...prev };
        delete newProgress[file.document_name];
        return newProgress;
      });
    }
  };

  const getStatusBadge = (status) => {
    if (!isStatusBadgeVisible) return null;
    
    const statusColor = status?.color_code;
    const statusName = status?.display_name;
    const statusColorRgba = convertHexToRgba(statusColor, 0.1);
    const statusColorStyle = {
      backgroundColor: statusColorRgba,
      borderColor: statusColor,
      color: statusColor,
    };
    return <span className="status-badge" style={statusColorStyle}>{statusName}</span>;
  } 

  const handleClose = () => {
    setComments("");
    onClose();
  };

  // Check if the pay request can be approved/rejected (write access + status 5 - PENDING COMP ADMIN REVIEW)
  const canApproveReject =
    writeAccess === 1 && timesheetDetails?.status?.timesheet_status_mstr_key === 5;

  // Handle approve button click - show confirmation dialog
  const handleApprove = () => {
    setConfirmationPopup({
      title: "Confirm Approval",
      desc: "Are you sure you want to approve this pay request?",
      isVisible: true,
      type: "APPROVE_CONFIRM",
      actionBtn: APPROVE_BTN_ACTIONS(onCancelClick, () => onSubmitClick("4")),
    });
  };

  // Handle reject button click - show confirmation dialog
  const handleReject = () => {
    setConfirmationPopup({
      title: "Confirm Rejection",
      desc: "Are you sure you want to reject this pay request?",
      isVisible: true,
      type: "REJECT_CONFIRM",
      actionBtn: REJECT_BTN_ACTIONS(onCancelClick, () => onSubmitClick("3")),
    });
  };

  // Cancel confirmation dialog
  const onCancelClick = () => {
    setConfirmationPopup({
      title: "",
      desc: "",
      isVisible: false,
      type: "",
      actionBtn: [],
    });
  };

  // Submit approval/rejection to API
  const onSubmitClick = async (status) => {
    if (!cookiesData?.out_user_id) return;
    setIsSubmitting(true);

    try {
      const response = await insertTimesheetApproval(
        cookiesData,
        dispatch,
        [{ id: timesheetMstrKey }],
        status,
        comments
      );

      if (response?.data?.out_parameters?.out_ret_code === 0) {
        const message = status === "4" ? "Pay request approved successfully" : "Pay request rejected successfully";
        dispatch(setNewMessage({ message: message, type: "Success", show: true }));
        if (onRefresh) {
          onRefresh();
        }
        handleClose();
      } else {
        const errorMessage = response?.data?.out_parameters?.out_error_msg || "Failed to update pay request status";
        dispatch(setNewMessage({ message: errorMessage, type: "Error", show: true }));
      }
    } catch (error) {
      console.error("Error submitting approval:", error);
      dispatch(setNewMessage({ message: "An error occurred while processing your request", type: "Error", show: true }));
    } finally {
      setIsSubmitting(false);
      onCancelClick();
    }
  };

  const ModalTitle = () => {
    return (
      <div className="modal-header">
        <div className="modal-title-container">
          <span className="modal-title">PAY REQUEST DETAILS</span>
        </div>
      </div>
    );
  };

  return (
    <>
      <Window
        key={`window-${visible}`}
        visible={visible}
        onClose={handleClose}
        title={<ModalTitle />}
        width={625}
        height={730}
        className="view-pay-request-dialog"
        modal={true}
        closeIcon={true}
        backdrop={true}
      >
        <div className="dialog-content">
          {isLoading ? (
            <LoadingPanel gridId="pay-request-detailed-grid" />
          ) : (
            <>
              <div className="pay-request-detailed-progress-container">
                <StepProgressBar
                  steps={mapApiDataToStepDetails(progressDetails)}
                  localTimezone={timesheetDetails?.local_timezone}
                />
              </div>
              <div className="pay-request-detailed-data-container">
                {CONTRACT_LIMIT_MESSAGES[timesheetDetails?.contract_limit_exceeded_type] && (
                  <div className="pay-request-detailed-warning-container">
                    {CONTRACT_LIMIT_MESSAGES[timesheetDetails?.contract_limit_exceeded_type]}
                  </div>
                )}

                {/* Payment Estimated Warning */}
                {/* {(timesheetDetails?.rate_type === 'step' || timesheetDetails?.rate_type === 'cliff' || timesheetDetails?.rate_type === 'multisite') && (
                                    <div className="pay-request-detailed-warning-container">
                                        The payment amount is estimated and may be adjusted once all pay requests for this period are submitted and processed.
                                    </div>
                                )} */}

                {/* Provider Section */}
                <div className="view-pay-request-container border-container">
                  <div className="view-pay-request-provider">
                    <div className="view-pay-request-provider-icon">
                      <i className="fas fa-user"></i>
                    </div>
                    <div className="view-pay-request-provider-container">
                      <div className="field-label">Provider</div>
                      <div className="field-value">
                        {timesheetDetails?.provider_name} ({timesheetDetails?.position_type_name})
                      </div>
                    </div>
                  </div>
                </div>

                {/* Location | Specialty */}
                <div className="view-pay-request-container">
                  <div className="view-pay-request-location border-container">
                    <div className="field-label">Location</div>
                    <div className="field-value">{timesheetDetails?.location_name}</div>
                  </div>
                  <div className="view-pay-request-specialty border-container">
                    <div className="field-label">Specialty</div>
                    <div className="field-value">{timesheetDetails?.specialty_name}</div>
                  </div>
                </div>

                {/* Pay Request Type | Pay Request Name */}
                <div className="view-pay-request-container">
                  <div className="view-pay-request-type border-container">
                    <div className="field-label">Pay Request Type</div>
                    <div className="field-value">{timesheetDetails?.contract_type}</div>
                  </div>
                  <div className="view-pay-request-name border-container">
                    <div className="field-label">Pay Request Name</div>
                    <div className="field-value">{timesheetDetails?.contract_name}</div>
                  </div>
                </div>

                {/* Total Quantity */}
                <div className="view-pay-request-container">
                  <div className="view-pay-request-hours border-container">
                    <div className="field-label">Total Quantity</div>
                    <div className="field-value">{formatTimesheetQuantity(timesheetDetails)}</div>
                  </div>
                  <div className="view-pay-request-budget border-container">
                    <div className="field-label">
                      Total Amount
                      {timesheetDetails?.rate_type === "step" ||
                      timesheetDetails?.rate_type === "cliff" ||
                      timesheetDetails?.rate_type === "multisite" ? (
                        <i
                          className="fa fa-exclamation-triangle"
                          style={{ color: "#E18601", marginLeft: "0.5rem" }}
                        ></i>
                      ) : (
                        ""
                      )}
                    </div>
                    <div className="field-value">$ {formatBudget(timesheetDetails?.budget)}</div>
                  </div>
                </div>

                {/* Pay Request Status | Status Changed Date */}
                <div className="view-pay-request-container">
                  <div className="view-pay-request-status border-container">
                    <div className="field-label">Pay Request Status</div>
                    <div className="field-value">
                      {getStatusBadge(timesheetDetails?.status)}
                    </div>
                  </div>
                  <div className="view-pay-request-status-date border-container">
                    <div className="field-label">Status Changed Date</div>
                    <div className="field-value">
                      {timesheetDetails?.status?.value === "new"
                        ? formatProviderLocalDate(timesheetDetails?.status_changed_date)
                        : formatDate(timesheetDetails?.status_changed_date)}
                    </div>
                  </div>
                </div>

                {/* Service Date | Post Date */}
                <div className="view-pay-request-container">
                  <div className="view-pay-request-service-date border-container">
                    <div className="field-label">Service Date</div>
                    <div className="field-value">{formatProviderLocalDate(timesheetDetails?.service_date)}</div>
                  </div>
                  <div className="view-pay-request-post-date border-container">
                    <div className="field-label">Post Date</div>
                    <div className="field-value">{formatProviderLocalDate(timesheetDetails?.post_date)}</div>
                  </div>
                </div>

                {/* Description */}
                <div className="view-pay-request-container border-container">
                  <div className="view-pay-request-description">
                    <div className="field-label">Description</div>
                    <div className="field-value" style={{ color: timesheetDetails?.ts_description ? "" : "#999999" }}>
                      {timesheetDetails?.ts_description || "No description"}
                    </div>
                  </div>
                </div>

                {/* Approver Comments */}
                {timesheetDetails?.comments &&
                  timesheetDetails.comments.filter((comment) => comment.comment && comment.comment.trim() !== "")
                    .length > 0 && (
                    <div className="view-pay-request-container border-container">
                      <div className="view-pay-request-comments">
                        <div className="field-label">Approver Comments</div>
                        <div className="comments-list">
                          {timesheetDetails.comments
                            .filter((comment) => comment.comment && comment.comment.trim() !== "")
                            .map((comment, index) => (
                              <div key={index} className="comment-item">
                                <div className="comment-approver">
                                  <i className="fas fa-user-circle comment-icon"></i>
                                  <span className="approver-name">{comment.approver || "Unknown Approver"}</span>
                                </div>
                                <div className="comment-text">{comment.comment}</div>
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  )}

                {/* Documents */}
                {timesheetDetails?.documents && timesheetDetails.documents.length > 0 && (
                  <div className="view-pay-request-container border-container" style={{ marginBottom: "1rem" }}>
                    <div className="view-pay-request-documents">
                      <div className="field-label">Documents</div>
                      <div className="documents-list">
                        {timesheetDetails.documents.map((doc, index) => (
                          <div key={index} className="document-item">
                            <div className="document-info">
                              <i className={`fas ${getFileIcon(doc.document_type)} document-icon`}></i>
                              <div className="document-details">
                                <div className="document-name">{doc.original_name}</div>
                                <div className="document-size">{formatFileSize(doc.document_size)}</div>
                              </div>
                            </div>
                            <div className="document-actions">
                              <Tooltip anchorElement="target" position="top" openDelay={100}>
                                <Button
                                  className="document-action-btn view-btn"
                                  onClick={() => handleFileView(doc)}
                                  title="View file"
                                >
                                  <i className="fas fa-eye"></i>
                                </Button>
                              </Tooltip>
                              <Tooltip anchorElement="target" position="top" openDelay={100}>
                                <Button
                                  className="document-action-btn download-btn"
                                  onClick={() => handleFileDownload(doc)}
                                  title="Download file"
                                  disabled={
                                    downloadProgress[doc.document_name] !== undefined &&
                                    downloadProgress[doc.document_name] < 100
                                  }
                                >
                                  {downloadProgress[doc.document_name] !== undefined &&
                                  downloadProgress[doc.document_name] < 100 ? (
                                    <i className="fas fa-spinner fa-spin"></i>
                                  ) : (
                                    <i className="fas fa-download"></i>
                                  )}
                                </Button>
                              </Tooltip>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Comments Section - Only for PENDING COMP ADMIN REVIEW status */}
                {canApproveReject && (
                  <div className="view-pay-request-container border-container">
                    <div className="view-pay-request-comments-input">
                      <div className="field-label">Comments</div>
                      <TextArea
                        value={comments}
                        onChange={(e) => setComments(e.value)}
                        placeholder="Enter comments (optional)"
                        className="comments-textarea"
                        rows={3}
                        style={{ width: "100%" }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Action Buttons - Only for PENDING COMP ADMIN REVIEW status */}
        {canApproveReject && !isLoading && (
          <div className="action-buttons-container">
            <Button 
              className="k-button reject-button" 
              onClick={handleReject}
              disabled={isSubmitting}
            >
              Reject
            </Button>
            <Button 
              className="k-button approve-button" 
              onClick={handleApprove}
              disabled={isSubmitting}
            >
              Approve
            </Button>
          </div>
        )}
      </Window>

      {/* Confirmation Dialog */}
      <DialogComponent
        title={confirmationPopup.title}
        onCancelConfirm={onCancelClick}
        width={"450"}
        height={"auto"}
        disabled={isSubmitting}
        className={"Dialog-Approve-Reject"}
        desc={confirmationPopup.desc}
        visible={confirmationPopup.isVisible}
        actionBtn={confirmationPopup.actionBtn}
        titleClassName={confirmationPopup.type === "APPROVE_CONFIRM" ? "approve-confirm" : "reject-confirm"}
        closeIcon={false}
      />
      {fileViewerVisible && (
        <FileViewerDialog
          visible={fileViewerVisible}
          onClose={() => {
            setFileViewerVisible(false);
            setSelectedFile(null);
          }}
          file={selectedFile}
        />
      )}
    </>
  );
};

export default PayRequestDetailed;
